import type * as ngtoaster from 'ngtoaster';
import type {ITimeoutService} from 'angular';

/**
 * @ngdoc service
 * @name toasterService
 * @module portaro.services
 *
 * @require toaster
 * @require $timeout
 *
 * @description
 * Service provides options to show toaster popup at the edge of the screen.
 * angular $timeout service is required to safely start digest cycle, when this service is used outside of angular context
 */
export default class ToasterService {
    public static readonly serviceName = 'toasterService';
    private readonly defaultTitle = '';
    private readonly defaultTimeout = 10_000;
    private readonly errorToastTimeout = 15_000;

    /*@ngInject*/
    constructor(private toaster: ngtoaster.IToasterService, private $timeout: ITimeoutService) {
    }

    /**
     * @ngdoc method
     * @name toasterService#showWarning
     *
     * @param {string} text Text to show.
     * @param {string=} title Optional toaster title.
     *
     * @description
     * Shows yellow warning message.
     */
    public showWarning(text: string, title?: string) {
        this.$timeout(() => this.toaster.warning(title || this.defaultTitle, text, this.defaultTimeout));
    }

    /**
     * @ngdoc method
     * @name toasterService#showError
     *
     * @param {string} text Text to show.
     * @param {string=} title Optional toaster title.
     *
     * @description
     * Shows red error message.
     */
    public showError(text: string, title?: string) {
        this.$timeout(() => this.toaster.error(title || this.defaultTitle, text, this.errorToastTimeout));
    }

    /**
     * @ngdoc method
     * @name toasterService#showSuccess
     *
     * @param {string} text Text to show.
     * @param {string=} title Optional toaster title.
     *
     * @description
     * Shows green success message.
     */
    public showSuccess(text: string, title?: string) {
        this.$timeout(() => this.toaster.success(title || this.defaultTitle, text, this.defaultTimeout));
    }

    /**
     * @ngdoc method
     * @name toasterService#showWait
     *
     * @param {string} text Text to show.
     * @param {string=} title Optional toaster title.
     *
     * @description
     * Shows wait message.
     */
    public showWait(text: string, title?: string) {
        this.$timeout(() => this.toaster.wait(title || this.defaultTitle, text, this.defaultTimeout));
    }

    /**
     * @ngdoc method
     * @name toasterService#showInfo
     *
     * @param {string} text Text to show.
     * @param {string=} title Optional toaster title.
     *
     * @description
     * Shows blue info message.
     */
    public showInfo(text: string, title?: string) {
        this.$timeout(() => this.toaster.info(title || this.defaultTitle, text, this.defaultTimeout));
}

}
